import { Component } from '@theme/component';
import {
  buildProductSearchUrl,
  debounce,
  onDocumentLoaded,
  pickBroadenedSearchUrl,
  startViewTransition,
  toExactMatchSearchQuery,
} from '@theme/utilities';
import { FilterUpdateEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Search query parameter (same as predictive search and facets).
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * A custom element that allows users to search collection products using the full store search
 * (same as predictive search). Uses the Section Rendering API so the main-collection section
 * is rendered with search context—same product cards as the collection (from collection.json).
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} resetButton - The reset button element.
 * @extends {Component<Refs>}
 */
class CollectionSearchInputComponent extends Component {
  requiredRefs = ['searchInput', 'resetButton'];

  #controller = new AbortController();
  #commitSearchOnIdle = () => {};

  /**
   * Get the section ID for Section Rendering (main-collection section).
   * @returns {string} The section ID
   */
  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  connectedCallback() {
    super.connectedCallback();

    // A fresh controller per connect: section morphs detach/reattach this
    // element, and listeners registered with an already-aborted signal (from
    // the previous disconnect) would never fire again.
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#commitSearchOnIdle = debounce(() => {
      // useTransition:true → the swap from the previous grid to the new results
      // is a single crossfade. Without it, morphing the full collection's cards
      // into the search results rewrites each card in place (image/price/title),
      // which the shopper sees as the grid flickering for ~1s after they stop
      // typing. The crossfade hides that churn behind one smooth transition.
      this.#performSearch({ useTransition: true });
    }, this.#getDebounceMs());

    // If the URL has a search term on load, the server rendered the FULL
    // collection (the collection route ignores q), so re-render with search
    // context. Deferred to document-ready and gated on the section's
    // data-search-context marker: connect timing varies (element upgrade can
    // run mid-parse or repeat after morphs) and #performSearch would
    // early-return because the URL already matches the term.
    onDocumentLoaded(() => this.#syncSearchContextOnLoad());

    // Section Rendering API requests have a blank request.query_string, so every
    // section morph re-renders the input with value="" — restore the active term.
    // (facets dispatches section-rendered on document, results-list on window.)
    document.addEventListener('section-rendered', this.#restoreTermAfterRender, { signal });
    window.addEventListener('section-rendered', this.#restoreTermAfterRender, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * On load with a q param: if the visible section isn't in search context
   * (no data-search-context marker on results-list), render it. Idempotent —
   * safe to run however many times connect/morph cycles happen.
   */
  async #syncSearchContextOnLoad() {
    // Clears the anti-flash flag set by the inline script in main-collection.liquid.
    const clearPending = () => document.documentElement.classList.remove('collection-q-pending');
    const query = new URL(window.location.href).searchParams.get(SEARCH_QUERY);
    if (!query || query.trim() === '') return clearPending();
    this.#showResetButton();
    const input = this.refs.searchInput;
    if (input && input.value.trim() === '') input.value = query;
    const section = document.querySelector(`results-list[section-id="${this.sectionId}"]`);
    if (section?.hasAttribute('data-search-context')) return clearPending();
    await this.#renderSectionWithSearchURL(query, false);
    clearPending();
  }

  /**
   * Re-fill the input from the URL's q param after a section morph blanked it
   * (never while the user is typing in it, and never when q was cleared).
   * morphSection is async and render callers don't await it, so the morph can
   * land after the section-rendered event — retry across a few frames, same
   * idiom as the grid price re-sort.
   */
  #restoreTermAfterRender = () => {
    const attempt = () => {
      const input = this.refs.searchInput;
      if (!input || document.activeElement === input) return;
      if (input.value.trim() !== '') return;
      const query = new URL(window.location.href).searchParams.get(SEARCH_QUERY);
      if (!query) return;
      input.value = query;
      this.#showResetButton();
    };
    attempt();
    requestAnimationFrame(attempt);
    setTimeout(attempt, 100);
    setTimeout(attempt, 400);
  };

  /**
   * Handles search input with debouncing.
   */
  handleSearchInput = () => {
    const hasTerm = this.#getSearchTerm().length > 0;
    if (hasTerm) {
      this.#showResetButton();
    } else {
      this.#hideResetButton();
    }
    this.#commitSearchOnIdle();
  };

  /**
   * Handles keyboard events (Escape clear, Enter submit).
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      if (this.refs.searchInput.value === '') {
        this.refs.searchInput.blur();
      } else {
        this.handleClearClick();
      }
    } else if (event.key === 'Enter') {
      // Idle pause is the single commit trigger.
      event.preventDefault();
    }
  };

  /**
   * Handles clear button click: restore collection grid and URL.
   */
  handleClearClick = () => {
    this.refs.searchInput.value = '';
    this.refs.searchInput.focus();
    this.#hideResetButton();
    this.#performSearch({ useTransition: false });
  };
  /**
   * @returns {number} Debounce delay in milliseconds.
   */
  #getDebounceMs() {
    const rawValue = Number.parseInt(this.getAttribute('search-debounce-ms') || '', 10);
    if (Number.isNaN(rawValue)) return 250;
    return Math.max(rawValue, 0);
  }

  /**
   * @returns {boolean} Whether current URL already reflects current search term.
   */
  #isURLSyncedWithSearchTerm() {
    const url = new URL(window.location.href);
    const currentURLTerm = (url.searchParams.get(SEARCH_QUERY) || '').trim();
    return currentURLTerm === this.#getSearchTerm();
  }


  /**
   * @returns {string} Current search term from input, trimmed (raw for URL).
   * Defensive: refs can be empty when connect runs mid-parse — a throw here
   * would abort connectedCallback and leave the component without listeners.
   */
  #getSearchTerm() {
    return this.refs.searchInput?.value?.trim() ?? '';
  }

  /**
   * Builds the store search URL used to render the main-collection section in
   * search context (collection card styling).
   *
   * By default the term is exact-matched (quoted — see toExactMatchSearchQuery),
   * same as the /search page: "rhum" matches the ~13 actual rhums, not the ~330
   * wines that merely mention rhum in their tasting notes (which, once sorted by
   * price, would surface Petrus at the top). Pass { fuzzy: true } for the
   * zero-results fallback, which searches loosely with prefix matching.
   * @param {string} term - Search term
   * @param {{ fuzzy?: boolean }} [opts]
   * @returns {URL}
   */
  #getSearchContextURL(term, { fuzzy = false } = {}) {
    const route =
      typeof Theme !== 'undefined' && Theme?.routes?.search_url ? Theme.routes.search_url : '/search';
    // Exact-match by default ("rhum" → the ~13 real rhums, not the ~330 that
    // mention it in tasting notes); { fuzzy: true } for the zero/sparse-results
    // fallback (bare term + prefix matching). Active sort + filters are carried
    // over so search re-renders don't silently undo the facet state.
    return buildProductSearchUrl({
      route,
      origin: location.origin,
      query: fuzzy ? term : toExactMatchSearchQuery(term),
      prefix: fuzzy,
      sourceParams: new URL(window.location.href).searchParams,
    });
  }

  /**
   * Builds the collection page URL (current path, optional q, no page).
   * Preserves the active sort and filters so typing a new term doesn't
   * silently reset them while the sort/filter UI still shows them active.
   * @param {string} [searchTerm] - If provided, add q param
   * @returns {string}
   */
  #getCollectionPageURL(searchTerm) {
    const url = new URL(window.location.href);
    if (searchTerm) {
      url.searchParams.set(SEARCH_QUERY, searchTerm);
    } else {
      url.searchParams.delete(SEARCH_QUERY);
    }
    url.searchParams.delete('page');
    return url.toString();
  }

  /**
   * Performs the search: update URL and re-render the main-collection section.
   * When there is a search term we pass the search URL so the section renders with search.results
   * and the same product card blocks as the collection (collection.json).
   */
  async #performSearch({ useTransition = true } = {}) {
    const searchTerm = this.#getSearchTerm();

    // Avoid extra morph/event churn when idle timer fires with no effective changes.
    if (this.#isURLSyncedWithSearchTerm()) return;

    this.#updateURL(searchTerm);

    if (searchTerm.length > 0) {
      this.#showResetButton();
      await this.#renderSectionWithSearchURL(searchTerm, useTransition);
    } else {
      this.#hideResetButton();
      await this.#renderSectionWithCurrentURL(useTransition);
    }

    this.#dispatchFilterUpdate();
  }

  /**
   * Updates the URL with the search query (same param as predictive search).
   * @param {string} searchTerm - The search term (empty = clear q)
   */
  #updateURL(searchTerm) {
    const url = this.#getCollectionPageURL(searchTerm || undefined);
    history.replaceState({}, '', url);
  }

  /**
   * Dispatch FilterUpdate so facets and results-list stay in sync with URL.
   */
  #dispatchFilterUpdate() {
    const url = new URL(window.location.href);
    const queryParams = new URLSearchParams(url.search);
    this.dispatchEvent(new FilterUpdateEvent(queryParams));
  }

  /**
   * Re-renders the main-collection section with the search URL so Shopify renders
   * the section in search context (search.results) with the collection's product card blocks.
   * @param {string} term - Search term
   */
  async #renderSectionWithSearchURL(term, useTransition = true) {
    const url = await pickBroadenedSearchUrl(
      sectionRenderer,
      this.sectionId,
      this.#getSearchContextURL(term),
      this.#getSearchContextURL(term, { fuzzy: true })
    );

    if (useTransition && !this.closest('dialog')) {
      await startViewTransition(
        () => sectionRenderer.renderSection(this.sectionId, { url, cache: true }),
        ['product-grid']
      );
    } else {
      await sectionRenderer.renderSection(this.sectionId, { url, cache: true });
    }
    this.#restoreTermAfterRender();
  }

  /**
   * Re-renders the main-collection section with the current URL (collection, no q).
   */
  async #renderSectionWithCurrentURL(useTransition = true) {
    const viewTransition = useTransition && !this.closest('dialog');

    if (viewTransition) {
      await startViewTransition(
        () => sectionRenderer.renderSection(this.sectionId),
        ['product-grid']
      );
    } else {
      await sectionRenderer.renderSection(this.sectionId);
    }
  }

  #showResetButton() {
    const { resetButton } = this.refs;
    resetButton.style.opacity = '1';
    resetButton.style.visibility = 'visible';
    resetButton.style.pointerEvents = 'auto';
  }

  #hideResetButton() {
    const { resetButton } = this.refs;
    resetButton.style.opacity = '0';
    resetButton.style.visibility = 'hidden';
    resetButton.style.pointerEvents = 'none';
  }
}

if (!customElements.get('collection-search-input')) {
  customElements.define('collection-search-input', CollectionSearchInputComponent);
}
